/**
 * 商品一覧クリック計測（GA4 / GTM 用 dataLayer プッシュ）
 *
 * - view_item_list : [data-item-list] の中に [data-list-item] が描画されたら、未送信のカードだけをまとめて送る
 * - select_item    : カード内の商品詳細リンク（/products/）クリックで送る（中クリック含む）
 * - window.fsListAttr(productId) : 直前にクリックした一覧の情報を sessionStorage から返す（view_item / add_to_cart 用）
 *
 * 一覧コンテナに必要な属性:
 *   data-item-list data-list-id data-list-name data-list-page data-page-size data-list-sort data-list-filtered
 * 商品カードに必要な属性:
 *   data-list-item data-item-id data-item-name data-item-brand data-item-category data-item-variant data-price
 */
(() => {
  window.dataLayer = window.dataLayer || [];

  const sent = new WeakSet(); // 送信済みのカード
  const KEY = 'fs_list_attr';
  const TTL = 30 * 60 * 1000;

  // display:none のタブや非表示セクションの中は「描画された」とみなさない
  const isRendered = (el) => el.getClientRects().length > 0;

  const cardsOf = (list) => [...list.querySelectorAll('[data-list-item]')];

  // 同じ item_list_name がページ内に複数ある場合、2つ目以降に ":2" ":3" を付ける
  const resolveNames = () => {
    const seen = {};
    document.querySelectorAll('[data-item-list]').forEach((list) => {
      if (!isRendered(list)) return;
      const base = list.dataset.listName || '';
      seen[base] = (seen[base] || 0) + 1;
      list.dataset.fsListName = seen[base] > 1 ? `${base}:${seen[base]}` : base;
    });
  };
  const nameOf = (list) => list.dataset.fsListName || list.dataset.listName || '';

  // 実際のレイアウトから列数を数える（1行目のカードと同じ高さにあるカードの数）
  const columns = (list) => {
    const cards = cardsOf(list);
    if (!cards.length) return 1;
    const top = cards[0].getBoundingClientRect().top;
    return cards.filter((c) => Math.abs(c.getBoundingClientRect().top - top) < 2).length || 1;
  };

  const toItem = (card, list, cols) => {
    const cards = cardsOf(list);
    const page = Number(list.dataset.listPage || 1);
    const size = Number(list.dataset.pageSize || cards.length);
    const index = (page - 1) * size + cards.indexOf(card) + 1;
    return {
      item_id: card.dataset.itemId,
      item_name: card.dataset.itemName,
      item_brand: card.dataset.itemBrand,
      item_category: card.dataset.itemCategory,
      item_variant: card.dataset.itemVariant,
      price: Number(card.dataset.price),
      index,
      item_row: Math.ceil(index / cols),
      item_list_id: list.dataset.listId,
      item_list_name: nameOf(list),
    };
  };

  const common = (list, cols) => ({
    list_page: Number(list.dataset.listPage || 1),
    list_sort: list.dataset.listSort || '',
    list_filtered: list.dataset.listFiltered || 'no',
    list_columns: cols,
  });

  const push = (event, list, items, cols) => {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      ...common(list, cols),
      ecommerce: { item_list_id: list.dataset.listId, item_list_name: nameOf(list), items },
    });
  };

  // view_item_list：未送信のカードだけを一覧ごとにまとめて送る
  const flush = () => {
    resolveNames();
    document.querySelectorAll('[data-item-list]').forEach((list) => {
      if (!isRendered(list)) return;
      const cards = cardsOf(list).filter((c) => !sent.has(c));
      if (!cards.length) return;
      const cols = columns(list);
      cards.forEach((c) => sent.add(c));
      push('view_item_list', list, cards.map((c) => toItem(c, list, cols)), cols);
    });
  };

  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(flush, 300);
  };
  // AJAX での一覧差し替え（絞り込み・並べ替え・おすすめの遅延読み込み）と、タブ切り替え（aria-hidden）を拾う
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden'],
  });
  schedule();

  // select_item
  const onClick = (e) => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    const link = target.closest('a[href*="/products/"]');
    if (!link || target.closest('[data-no-select]')) return;
    const card = link.closest('[data-list-item]');
    const list = card && card.closest('[data-item-list]');
    if (!list) return;
    const cols = columns(list);
    const item = toItem(card, list, cols);
    push('select_item', list, [item], cols);
    try {
      const now = Date.now();
      const store = JSON.parse(sessionStorage.getItem(KEY) || '{}');
      Object.keys(store).forEach((k) => {
        if (!store[k] || now - store[k].t >= TTL) delete store[k];
      });
      const productId = card.dataset.productId || item.item_id;
      store[String(productId)] = {
        item_list_id: item.item_list_id,
        item_list_name: item.item_list_name,
        index: item.index,
        t: now,
      };
      sessionStorage.setItem(KEY, JSON.stringify(store));
    } catch (_) {}
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  // view_item / add_to_cart 用：直前にクリックした一覧の情報を取り出す
  window.fsListAttr = (productId) => {
    try {
      const a = JSON.parse(sessionStorage.getItem(KEY) || '{}')[String(productId)];
      return a && Date.now() - a.t < TTL
        ? { item_list_id: a.item_list_id, item_list_name: a.item_list_name, index: a.index }
        : {};
    } catch (_) {
      return {};
    }
  };
})();
